import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { styled, useTheme } from '@mui/material/styles';
import { AppBar, IconButton, Stack, Toolbar, useMediaQuery } from '@mui/material';
import { MIcon } from '../../../@material-extend';
import Breadcrumbs from '../../nav/Breadcrumbs';
import OfflineBanner from '../../../OfflineBanner';
import SearchBar from '../SearchBar';
import AvatarButton from './AvatarButton';

import useCollapseDrawer from '../../../../hooks/useCollapseDrawer';

const DRAWER_WIDTH = 240;
const COLLAPSE_WIDTH = 102;
const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  padding: theme.spacing(0, 1, 0, 2),
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5)
  }
}));

const HamburgerMenu = ({ onOpenSidebar }) => {
  const { breakpoints } = useTheme();
  const showHamburger = useMediaQuery(breakpoints.between('sm', 'lg'));
  const iconVariant = window.env.isMobileDevice ? 'primary-lightest' : 'primary';

  if (!showHamburger) {
    return;
  }

  return (
    <IconButton sx={{ mr: 0.25 }} onClick={onOpenSidebar}>
      <MIcon icon="menu" variant={iconVariant} />
    </IconButton>
  );
};
HamburgerMenu.propTypes = {
  onOpenSidebar: PropTypes.func
};

const Navbar = ({ onOpenSidebar }) => {
  const { breakpoints } = useTheme();
  const { isCollapse } = useCollapseDrawer();
  const isMobileDevice = window.env.isMobileDevice;
  const appBarColor = isMobileDevice ? 'primary' : 'inherit';

  const styleAppBar = useMemo(
    () => ({
      paddingTop: 'env(safe-area-inset-top)',
      boxShadow: 'none',
      // Fond blanc opaque sur desktop (évite l'effet de flou du contenu qui scroll
      // derrière le header). Sur mobile l'AppBar est en couleur `primary` (bleu),
      // donc rien à override.
      ...(!isMobileDevice && { bgcolor: 'background.paper' }),
      [breakpoints.up('lg')]: {
        width: `calc(100% - ${DRAWER_WIDTH + 1}px)`
      },
      ...(isCollapse && { width: { lg: `calc(100% - ${COLLAPSE_WIDTH}px)` } })
    }),
    [isMobileDevice, breakpoints, isCollapse]
  );

  return (
    <AppBar id="app-bar-content" sx={styleAppBar} color={appBarColor} enableColorOnDark>
      <OfflineBanner />
      <ToolbarStyle className="app-bar-style">
        <HamburgerMenu onOpenSidebar={onOpenSidebar} />

        <Breadcrumbs />

        <Stack id="app-bar-actions" direction="row" alignItems="center" gap={{ xs: 0.5, sm: 1.5 }}>
          {!window.env.isClockingApp && <SearchBar />}
          <AvatarButton />
        </Stack>
      </ToolbarStyle>
    </AppBar>
  );
};

Navbar.propTypes = {
  onOpenSidebar: PropTypes.func
};

export default Navbar;
